import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { loanOfficersQueryOptions } from '@/api/reference-api'
import type { DualControlResult } from '@/api/types'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { DualControlNotice, DualControlOutcome } from './dual-control'
import { GroupActionDialog } from './group-action-dialog'
import { useCreateGroup } from './mutations'
import { GROUP_PERMISSIONS, useGroupPermissions } from './permissions'

const today = () => new Date().toISOString().slice(0, 10)

const UNASSIGNED = 'unassigned'

/**
 * Forming a group.
 *
 * Dual-controlled, and the group code is not asked for: it is issued by the server, from the
 * branch and a sequence, and only when the creation actually executes. A request sitting in an
 * approval queue has not consumed a code — which is why this dialog cannot show one even after a
 * successful call that is awaiting a countersignature, and does not pretend to.
 */
export function NewGroupDialog({
  branchId,
  open,
  onOpenChange,
}: {
  branchId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const permissions = useGroupPermissions()
  const [name, setName] = useState('')
  const [formationDate, setFormationDate] = useState(today)
  const [officerId, setOfficerId] = useState(UNASSIGNED)
  const [result, setResult] = useState<DualControlResult | null>(null)

  const officers = useQuery(loanOfficersQueryOptions(open ? branchId : null))
  const create = useCreateGroup()

  useEffect(() => {
    if (!open) {
      setName('')
      setFormationDate(today())
      setOfficerId(UNASSIGNED)
      setResult(null)
      create.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const trimmedName = name.trim()

  return (
    <GroupActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Form a group"
      description="The group is created in Forming. It becomes Active once it has a leader."
      notice={
        <DualControlNotice
          action="Forming a group"
          approvePermission={GROUP_PERMISSIONS.createApprove}
          canCountersign={permissions.canCountersignCreate}
        />
      }
      outcome={
        result ? (
          <DualControlOutcome
            result={result}
            awaitingDescription={`Forming ${trimmedName} is waiting for a countersignature. No group code has been issued — a request in a queue has not consumed one.`}
            appliedDescription={`${trimmedName} exists, in Forming, with a group code issued by the branch sequence.`}
          />
        ) : null
      }
      confirmLabel="Form group"
      isPending={create.isPending}
      disabled={trimmedName === '' || formationDate === '' || branchId === null}
      error={create.isError ? create.error : null}
      onConfirm={() => {
        if (branchId === null) {
          return
        }

        create.mutate(
          {
            branchId,
            name: trimmedName,
            formationDate,
            loanOfficerId: officerId === UNASSIGNED ? null : officerId,
          },
          { onSuccess: setResult },
        )
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="new-group-name">Name</Label>
        <Input
          id="new-group-name"
          value={name}
          autoComplete="off"
          autoFocus
          placeholder="Makola Traders A"
          onChange={(event) => setName(event.target.value)}
          aria-describedby="new-group-name-help"
        />
        <p id="new-group-name-help" className="text-xs text-muted">
          The group code is issued by the server when the group is actually created. It is never
          typed here.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="new-group-formed">Formation date</Label>
        <Input
          id="new-group-formed"
          type="date"
          value={formationDate}
          max={today()}
          onChange={(event) => setFormationDate(event.target.value)}
          aria-describedby="new-group-formed-help"
        />
        <p id="new-group-formed-help" className="text-xs text-muted">
          A group cannot have been formed on a date that has not happened.
        </p>
      </div>

      {officers.data && officers.data.length > 0 ? (
        <div className="space-y-1.5">
          <Label htmlFor="new-group-officer">Loan officer</Label>
          <Select value={officerId} onValueChange={setOfficerId}>
            <SelectTrigger id="new-group-officer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED}>Assign later</SelectItem>
              {officers.data.map((staff) => (
                <SelectItem key={staff.userId} value={staff.userId}>
                  {staff.fullName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted">
            Optional at formation. Assigning one afterwards is a separate, countersigned action.
          </p>
        </div>
      ) : null}
    </GroupActionDialog>
  )
}
